import { z } from 'zod';

import {
  customerIdentifierStatusValues,
  customerIdentifierTypeValues,
  customerStatusValues,
  customerSyncStatusValues,
  type AddCustomerIdentifierRequest,
  type AddCustomerIdentifierResponse,
  type CreateCustomerRequest,
  type CreateCustomerResponse,
  type CustomerDto,
  type CustomerIdentifierDto,
  type CustomerIdentifierMatchDto,
  type CustomerIdentifierSnapshot,
  type CustomerIdentifierStatus,
  type CustomerIdentifierType,
  type CustomerSnapshot,
  type CustomerSnapshotForPurchaseRequest,
  type CustomerSnapshotForPurchaseResponse,
  type CustomerStatus,
  type CustomerSyncStatus,
  type DeactivateCustomerIdentifierRequest,
  type DeactivateCustomerIdentifierResponse,
  type DeactivateCustomerRequest,
  type DeactivateCustomerResponse,
  type GetCustomerRequest,
  type GetCustomerResponse,
  type ListCustomerIdentifiersRequest,
  type ListCustomerIdentifiersResponse,
  type ListCustomersRequest,
  type ListCustomersResponse,
  type RecordPurchaseReassignmentRequest,
  type RecordPurchaseReassignmentResponse,
  type ResolveCustomerMatchByIdentifierRequest,
  type ResolveCustomerMatchByIdentifierResponse,
  type SetDefaultCustomerIdentifierRequest,
  type SetDefaultCustomerIdentifierResponse,
  type UpdateCustomerIdentifierRequest,
  type UpdateCustomerIdentifierResponse,
  type UpdateCustomerRequest,
  type UpdateCustomerResponse,
} from './customers.dto.js';

const timestampSchema = z.iso.datetime();
const displayNameSchema = z.string().trim().min(1).max(200);
const receiptNumberSchema = z.string().trim().min(1).max(100);
const identifierValueSchema = z.string().trim().min(1).max(100);
const maskedValueSchema = z.string().trim().min(1);
const actionSchema = z.string().trim().min(1).max(100);

export const CustomerStatusSchema: z.ZodType<CustomerStatus> = z.enum(customerStatusValues);
export const CustomerSyncStatusSchema: z.ZodType<CustomerSyncStatus> = z.enum(customerSyncStatusValues);
export const CustomerIdentifierTypeSchema: z.ZodType<CustomerIdentifierType> = z.enum(
  customerIdentifierTypeValues,
);
export const CustomerIdentifierStatusSchema: z.ZodType<CustomerIdentifierStatus> = z.enum(
  customerIdentifierStatusValues,
);

export const CustomerDtoSchema: z.ZodType<CustomerDto> = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  displayName: displayNameSchema,
  status: CustomerStatusSchema,
  isPublic: z.boolean(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
  syncStatus: CustomerSyncStatusSchema.optional(),
  syncError: z.string().trim().min(1).nullable().optional(),
});

export const CustomerIdentifierDtoSchema: z.ZodType<CustomerIdentifierDto> = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  customerId: z.uuid(),
  type: CustomerIdentifierTypeSchema,
  valueMasked: maskedValueSchema,
  status: CustomerIdentifierStatusSchema,
  isDefault: z.boolean(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
  syncStatus: CustomerSyncStatusSchema.optional(),
  syncError: z.string().trim().min(1).nullable().optional(),
});

export const CustomerIdentifierSnapshotSchema: z.ZodType<CustomerIdentifierSnapshot> = z.object({
  type: CustomerIdentifierTypeSchema,
  value: identifierValueSchema,
});

export const CustomerSnapshotSchema: z.ZodType<CustomerSnapshot> = z.object({
  name: displayNameSchema,
});

export const CustomerIdentifierMatchDtoSchema: z.ZodType<CustomerIdentifierMatchDto> = z.object({
  customerId: z.uuid(),
  displayName: displayNameSchema,
  identifierId: z.uuid(),
});

export const CreateCustomerRequestSchema: z.ZodType<CreateCustomerRequest> = z.object({
  companyId: z.uuid(),
  displayName: displayNameSchema,
});

export const CreateCustomerResponseSchema: z.ZodType<CreateCustomerResponse> = z.object({
  customer: CustomerDtoSchema,
});

export const UpdateCustomerRequestSchema: z.ZodType<UpdateCustomerRequest> = z.object({
  companyId: z.uuid(),
  customerId: z.uuid(),
  displayName: displayNameSchema.optional(),
});

export const UpdateCustomerResponseSchema: z.ZodType<UpdateCustomerResponse> = z.object({
  customer: CustomerDtoSchema,
});

export const DeactivateCustomerRequestSchema: z.ZodType<DeactivateCustomerRequest> = z.object({
  companyId: z.uuid(),
  customerId: z.uuid(),
});

export const DeactivateCustomerResponseSchema: z.ZodType<DeactivateCustomerResponse> = z.object({
  customer: CustomerDtoSchema,
});

export const GetCustomerRequestSchema: z.ZodType<GetCustomerRequest> = z.object({
  companyId: z.uuid(),
  customerId: z.uuid(),
});

export const GetCustomerResponseSchema: z.ZodType<GetCustomerResponse> = z.object({
  customer: CustomerDtoSchema,
});

export const ListCustomersRequestSchema: z.ZodType<ListCustomersRequest> = z.object({
  companyId: z.uuid(),
  status: CustomerStatusSchema.optional(),
});

export const ListCustomersResponseSchema: z.ZodType<ListCustomersResponse> = z.object({
  items: z.array(CustomerDtoSchema),
});

export const AddCustomerIdentifierRequestSchema: z.ZodType<AddCustomerIdentifierRequest> = z.object({
  companyId: z.uuid(),
  customerId: z.uuid(),
  type: CustomerIdentifierTypeSchema,
  value: identifierValueSchema,
  isDefault: z.boolean().optional(),
});

export const AddCustomerIdentifierResponseSchema: z.ZodType<AddCustomerIdentifierResponse> = z.object({
  identifier: CustomerIdentifierDtoSchema,
});

export const UpdateCustomerIdentifierRequestSchema: z.ZodType<UpdateCustomerIdentifierRequest> = z.object({
  companyId: z.uuid(),
  customerId: z.uuid(),
  identifierId: z.uuid(),
  value: identifierValueSchema.optional(),
});

export const UpdateCustomerIdentifierResponseSchema: z.ZodType<UpdateCustomerIdentifierResponse> = z.object({
  identifier: CustomerIdentifierDtoSchema,
});

export const DeactivateCustomerIdentifierRequestSchema: z.ZodType<DeactivateCustomerIdentifierRequest> = z.object({
  companyId: z.uuid(),
  customerId: z.uuid(),
  identifierId: z.uuid(),
});

export const DeactivateCustomerIdentifierResponseSchema: z.ZodType<DeactivateCustomerIdentifierResponse> = z.object({
  identifier: CustomerIdentifierDtoSchema,
});

export const SetDefaultCustomerIdentifierRequestSchema: z.ZodType<SetDefaultCustomerIdentifierRequest> = z.object({
  companyId: z.uuid(),
  customerId: z.uuid(),
  identifierId: z.uuid(),
});

export const SetDefaultCustomerIdentifierResponseSchema: z.ZodType<SetDefaultCustomerIdentifierResponse> = z.object({
  identifier: CustomerIdentifierDtoSchema,
});

export const ListCustomerIdentifiersRequestSchema: z.ZodType<ListCustomerIdentifiersRequest> = z.object({
  companyId: z.uuid(),
  customerId: z.uuid(),
  status: CustomerIdentifierStatusSchema.optional(),
});

export const ListCustomerIdentifiersResponseSchema: z.ZodType<ListCustomerIdentifiersResponse> = z.object({
  items: z.array(CustomerIdentifierDtoSchema),
});

export const ResolveCustomerMatchByIdentifierRequestSchema: z.ZodType<ResolveCustomerMatchByIdentifierRequest> =
  z.object({
    companyId: z.uuid(),
    identifierType: CustomerIdentifierTypeSchema,
    identifierValue: identifierValueSchema,
  });

export const ResolveCustomerMatchByIdentifierResponseSchema: z.ZodType<ResolveCustomerMatchByIdentifierResponse> =
  z.object({
    match: CustomerIdentifierMatchDtoSchema.nullable(),
    needsOperatorConfirmation: z.boolean(),
  });

export const CustomerSnapshotForPurchaseRequestSchema: z.ZodType<CustomerSnapshotForPurchaseRequest> =
  z.object({
    requiresCustomerName: z.boolean(),
    customerDisplayName: displayNameSchema,
    identifierSnapshot: CustomerIdentifierSnapshotSchema.nullable().optional(),
  });

export const CustomerSnapshotForPurchaseResponseSchema: z.ZodType<CustomerSnapshotForPurchaseResponse> =
  z.object({
    customerSnapshot: CustomerSnapshotSchema.nullable(),
    identifierSnapshot: CustomerIdentifierSnapshotSchema.nullable(),
  });

export const RecordPurchaseReassignmentRequestSchema: z.ZodType<RecordPurchaseReassignmentRequest> =
  z.object({
    companyId: z.uuid(),
    actorUserId: z.uuid(),
    receiptNumber: receiptNumberSchema,
    purchaseId: z.uuid().nullable().optional(),
    occurredAt: timestampSchema.optional(),
    action: actionSchema.optional(),
    auditId: z.uuid().optional(),
  });

export const RecordPurchaseReassignmentResponseSchema: z.ZodType<RecordPurchaseReassignmentResponse> =
  z.object({
    recorded: z.literal(true),
  });
